import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, FileText, Link2, Unlink, User, ClipboardSignature } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { TraegerDot, useTraegerStatusMap, isTraegerMissing } from "@/components/TraegerStatusCard";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import WeiterbildungKundeDialog from "@/components/WeiterbildungKundeDialog";

export default function WeiterbildungKundenListe() {
  const [participants, setParticipants] = useState([]);
  const [classes, setClasses] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const navigate = useNavigate();
  const traegerMap = useTraegerStatusMap("participant");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const missingCount = participants.filter((p) => isTraegerMissing(traegerMap[p.participant_id])).length;
  const shown = onlyMissing ? participants.filter((p) => isTraegerMissing(traegerMap[p.participant_id])) : participants;

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, m] = await Promise.all([
        api.get("/weiterbildung/participants"),
        api.get("/weiterbildung/classes"),
        api.get("/weiterbildung/measures"),
      ]);
      setParticipants(p.data);
      setClasses(c.data);
      setMeasures(m.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const classById = (id) => classes.find((c) => c.class_id === id);
  const measureById = (id) => measures.find((m) => m.measure_id === id);

  const remove = async (p) => {
    if (!window.confirm(`Kunde ${p.name || `${p.first_name} ${p.last_name}`} endgültig löschen?`)) return;
    try {
      await api.delete(`/weiterbildung/participants/${p.participant_id}`);
      toast.success("Kunde gelöscht"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const unassign = async (p) => {
    try {
      await api.post(`/weiterbildung/participants/${p.participant_id}/unassign`);
      toast.success("Aus Klasse entfernt (Kunde bleibt erhalten)"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const assign = async (classId) => {
    try {
      await api.post(`/weiterbildung/participants/${assignTarget.participant_id}/assign`, { class_id: classId });
      toast.success("Klasse zugewiesen"); setAssignTarget(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openContractPdf = async (p) => {
    try {
      const res = await api.get(`/weiterbildung/participants/${p.participant_id}/contract.pdf`, { responseType: "blob" });
      window.open(window.URL.createObjectURL(res.data), "_blank");
    } catch (e) {
      let detail = e.message;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail || detail; } catch { /* keep default */ }
      } else detail = formatApiErrorDetail(e.response?.data?.detail) || detail;
      toast.error(detail);
    }
  };

  return (
    <div data-testid="wb-kunden-liste">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600 max-w-xl">
          Weiterbildungskunden werden hier zentral erfasst (Adresse, Weiterbildung + gebuchte Module) und
          anschließend einer Klasse zugewiesen.
        </p>
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-slate-900 text-white px-6 py-3 text-sm font-medium hover:bg-slate-800 flex items-center gap-2 shrink-0" data-testid="wb-kunde-erfassen-btn">
          <Plus size={16} strokeWidth={1.5} /> Kunde erfassen
        </button>
      </div>

      {Object.keys(traegerMap).length > 0 && (
        <div className="mb-4">
          <button onClick={() => setOnlyMissing(!onlyMissing)} data-testid="wb-filter-traeger-missing"
            className={`px-3 py-1.5 text-xs border transition-colors ${onlyMissing ? "border-amber-600 bg-amber-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
            Bildungsgutschein fehlt <span className="opacity-70 ml-1">{missingCount}</span>
          </button>
        </div>
      )}
      {loading && <div className="text-sm text-slate-500 text-center py-10">Lädt…</div>}

      {!loading && participants.length === 0 && (
        <div className="border border-dashed border-slate-300 p-16 text-center">
          <User size={48} strokeWidth={1} className="mx-auto text-slate-300 mb-4" />
          <div className="text-slate-500">Noch keine Weiterbildungskunden erfasst.</div>
        </div>
      )}

      <div className="border border-slate-200 divide-y divide-slate-100">
        {shown.map((p) => {
          const cls = classById(p.class_id);
          const measure = measureById(p.measure_id);
          const moduleCount = (p.module_ids || []).length;
          return (
            <div key={p.participant_id} className="p-4 flex items-center gap-4" data-testid={`wb-kunde-row-${p.participant_id}`}>
              <div className="flex-1 min-w-0">
                <Link to={`/weiterbildung/kunden/${p.participant_id}`} className="text-sm font-medium text-slate-900 hover:underline underline-offset-4" data-testid={`wb-kunde-akte-link-${p.participant_id}`}>{p.name || `${p.first_name} ${p.last_name}`}</Link>
                <TraegerDot kind="participant" id={p.participant_id} info={traegerMap[p.participant_id]} />
                <div className="text-xs text-slate-500 font-mono">{p.email}</div>
                {measure && <div className="text-[10px] text-slate-400 mt-1">{measure.name} · {moduleCount} Modul{moduleCount === 1 ? "" : "e"} gebucht</div>}
              </div>
              <div className="text-xs">
                {cls ? (
                  <span className="px-2 py-1 bg-slate-100 text-slate-700 font-mono" data-testid={`wb-kunde-klasse-${p.participant_id}`}>{cls.name}</span>
                ) : (
                  <span className="px-2 py-1 bg-amber-50 text-amber-700 font-mono" data-testid={`wb-kunde-klasse-${p.participant_id}`}>— nicht zugewiesen —</span>
                )}
              </div>
              <div className="flex gap-2">
                {cls ? (
                  <button onClick={() => unassign(p)} className="p-2 border border-slate-200 text-slate-700 hover:bg-slate-100" title="Aus Klasse entfernen" data-testid={`wb-kunde-unassign-${p.participant_id}`}>
                    <Unlink size={12} strokeWidth={1.5} />
                  </button>
                ) : (
                  <button onClick={() => setAssignTarget(p)} className="p-2 border border-slate-200 text-[#0a788c] hover:bg-slate-100" title="Klasse zuweisen" data-testid={`wb-kunde-assign-${p.participant_id}`}>
                    <Link2 size={12} strokeWidth={1.5} />
                  </button>
                )}
                <button onClick={() => openContractPdf(p)} className="p-2 border border-slate-200 text-[#0a788c] hover:bg-slate-100" title="Vertrag (PDF)" data-testid={`wb-kunde-pdf-${p.participant_id}`}>
                  <FileText size={12} strokeWidth={1.5} />
                </button>
                <button onClick={() => navigate(`/traegerbestaetigung?kind=participant&id=${p.participant_id}`)} className="p-2 border border-slate-200 text-[#0a788c] hover:bg-slate-100" title="Trägerbestätigung" data-testid={`wb-kunde-traeger-${p.participant_id}`}>
                  <ClipboardSignature size={12} strokeWidth={1.5} />
                </button>
                <button onClick={() => { setEditing(p); setDialogOpen(true); }} className="p-2 border border-slate-200 text-slate-700 hover:bg-slate-100" title="Bearbeiten" data-testid={`wb-kunde-edit-${p.participant_id}`}>
                  <Pencil size={12} strokeWidth={1.5} />
                </button>
                <button onClick={() => remove(p)} className="p-2 border border-slate-200 text-red-600 hover:bg-red-50" title="Löschen" data-testid={`wb-kunde-delete-${p.participant_id}`}>
                  <Trash2 size={12} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <WeiterbildungKundeDialog open={dialogOpen} onOpenChange={setDialogOpen} initialData={editing} onSaved={load} />

      {assignTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setAssignTarget(null)}>
          <div className="bg-white border border-slate-200 max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="wb-assign-class-modal">
            <div className="text-sm font-medium text-slate-900 mb-3">Klasse zuweisen für {assignTarget.name || assignTarget.first_name}</div>
            {classes.length === 0 && <div className="text-xs text-slate-400 italic">Noch keine Klassen angelegt.</div>}
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {classes.map((c) => (
                <button key={c.class_id} onClick={() => assign(c.class_id)} className="w-full text-left px-3 py-2 text-xs border border-slate-200 hover:bg-slate-50" data-testid={`wb-assign-class-option-${c.class_id}`}>
                  {c.name} <span className="text-slate-400">· {c.weiterbildung_name}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setAssignTarget(null)} className="mt-4 w-full text-xs border border-slate-300 py-2 hover:bg-slate-100">Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}
